import type {
  SeriesZonesOptionsObject,
  XAxisPlotBandsOptions,
  YAxisPlotLinesOptions,
} from "highcharts";

import type {
  ParameterTimeSeriesChartResult,
  ParameterTimeSeriesThreshold,
} from "@/lib/api";
import { interpolateIntersection } from "@/features/shared/widgets/graphs/helpers";

import {
  resolveThresholdBandColor,
  resolveThresholdLineColor,
  resolveThresholdPointColor,
} from "./threshold-colors";

/** True when `value` breaches the threshold (max: strictly above, min: strictly below). */
export function valueExceedsThreshold(
  value: number,
  threshold: ParameterTimeSeriesThreshold,
): boolean {
  if (threshold.threshold_type === "max") return value > threshold.value;
  if (threshold.threshold_type === "min") return value < threshold.value;
  return false;
}

/** Horizontal reference line drawn at each threshold value, colored by config. */
export function buildThresholdPlotLines(
  thresholds: ParameterTimeSeriesThreshold[] | undefined,
): YAxisPlotLinesOptions[] {
  if (!thresholds?.length) return [];
  return thresholds.map((threshold, index) => ({
    id: `threshold-${index}`,
    value: threshold.value,
    color: resolveThresholdLineColor(threshold.color),
    width: 2,
    zIndex: 5,
  }));
}

/**
 * Time bands highlighting intervals where a series crosses a min/max threshold.
 * Crossing times are interpolated between adjacent datapoints. Bands are computed per
 * (threshold, series) so multiple series in a group do not bleed into each other.
 */
export function buildThresholdPlotBands(
  thresholds: ParameterTimeSeriesThreshold[] | undefined,
  data: ParameterTimeSeriesChartResult[],
): XAxisPlotBandsOptions[] {
  if (!thresholds?.length) return [];

  const bands: XAxisPlotBandsOptions[] = [];

  // Overlapping bands are painted in push order (later = on top). A `max`
  // threshold shades everything above its value and a `min` everything below,
  // so a less-extreme threshold's band is a superset of a more-extreme one's.
  // Order them so the more-extreme threshold is pushed last and stays visible:
  // `max` ascending (higher value on top) and `min` descending (lower on top).
  const ordered = [...thresholds].sort((a, b) => {
    if (a.threshold_type !== b.threshold_type) {
      return a.threshold_type === "max" ? -1 : 1;
    }
    return a.threshold_type === "max" ? a.value - b.value : b.value - a.value;
  });

  for (const threshold of ordered) {
    const color = resolveThresholdBandColor(threshold.color);

    for (const result of data) {
      const datapoints = result.data?.datapoints;
      if (!datapoints?.length) continue;

      let current: XAxisPlotBandsOptions | undefined;

      datapoints.forEach((point, index, arr) => {
        const y = Number(point.value);
        if (Number.isNaN(y)) return;

        const x = +new Date(point.timestamp);
        const prev = arr[index - 1];
        const prevY = prev ? Number(prev.value) : NaN;
        const intersection =
          prev && !Number.isNaN(prevY)
            ? interpolateIntersection(
                +new Date(prev.timestamp),
                prevY,
                x,
                y,
                threshold.value,
              )
            : x;

        const exceeds = valueExceedsThreshold(y, threshold);

        if (!current && exceeds) {
          current = { color, from: intersection };
        }
        if (current && exceeds) {
          current.to = x;
        }
        if (current && !exceeds) {
          current.to = intersection;
          bands.push(current);
          current = undefined;
        }
      });

      if (current) {
        bands.push(current);
        current = undefined;
      }
    }
  }

  return bands;
}

/**
 * Highcharts series zones that recolor line segments / markers by Y value.
 * Zones are ordered ascending: min thresholds color the region below their value, a
 * default (uncolored) zone spans up to the first max, and each max colors the region
 * above it.
 */
export function buildThresholdZones(
  thresholds: ParameterTimeSeriesThreshold[] | undefined,
): SeriesZonesOptionsObject[] {
  if (!thresholds?.length) return [];

  const sorted = [...thresholds].sort((a, b) => a.value - b.value);
  const mins = sorted.filter((t) => t.threshold_type === "min");
  const maxes = sorted.filter((t) => t.threshold_type === "max");

  const minZones: SeriesZonesOptionsObject[] = mins.map((threshold) => ({
    value: threshold.value,
    color: resolveThresholdPointColor(threshold.color),
  }));

  const maxZones = maxes.reduce<SeriesZonesOptionsObject[]>(
    (acc, threshold, index, array) => {
      // First (uncolored) zone spans everything up to the lowest max value.
      if (acc.length === 0) acc.push({ value: threshold.value });
      const next = array[index + 1];
      const color = resolveThresholdPointColor(threshold.color);
      if (next) {
        acc.push({ value: next.value, color });
      } else {
        acc.push({ color });
      }
      return acc;
    },
    [],
  );

  return [...minZones, ...maxZones];
}
